import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Plus, Trash2, MapPin, Edit2, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSchedule = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', date: '', time: '', end_time: '', location: '', notes: '' });

  const getToken = () => localStorage.getItem('adminToken');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/schedule`, { headers: headers() });
      setItems(res.data);
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ title: '', date: '', time: '', end_time: '', location: '', notes: '' });
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${API}/admin/schedule/${editId}`, form, { headers: headers() });
        toast.success('Updated');
      } else {
        await axios.post(`${API}/admin/schedule`, form, { headers: headers() });
        toast.success('Added');
      }
      resetForm();
      fetchItems();
    } catch { toast.error('Failed to save'); }
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, date: item.date, time: item.time, end_time: item.end_time || '', location: item.location || '', notes: item.notes || '' });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await axios.delete(`${API}/admin/schedule/${id}`, { headers: headers() });
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Failed to delete'); }
  };

  // Group by date
  const today = new Date().toISOString().split('T')[0];
  const upcoming = items.filter(i => i.date >= today).sort((a, b) => a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date));
  const past = items.filter(i => i.date < today).sort((a, b) => b.date.localeCompare(a.date));

  const groupByDate = (list) => {
    const groups = {};
    list.forEach(item => {
      if (!groups[item.date]) groups[item.date] = [];
      groups[item.date].push(item);
    });
    return groups;
  };

  const formatDate = (d) => {
    if (d === today) return 'Today';
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    if (d === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  const upcomingGroups = groupByDate(upcoming);
  const pastGroups = groupByDate(past);

  return (
    <div className="p-6 lg:p-8" data-testid="admin-schedule-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#1F2937] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>My Schedule</h1>
          <p className="text-[#6B7280]">Your personal appointment book</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0F4C5C] text-white rounded-lg hover:bg-[#0F4C5C]/90 font-medium text-sm transition-colors"
          data-testid="add-schedule-btn">
          <Plus className="w-4 h-4" /> Add Appointment
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6" data-testid="schedule-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#1F2937]">{editId ? 'Edit' : 'New'} Appointment</h2>
            <button onClick={resetForm} className="text-[#6B7280] hover:text-[#1F2937]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#1F2937] mb-1">Title *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Dr. Ahmad - Crown fitting"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-sm"
                data-testid="schedule-title" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-1">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-sm"
                data-testid="schedule-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">Start *</label>
                <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-sm"
                  data-testid="schedule-time" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1F2937] mb-1">End</label>
                <input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-sm"
                  data-testid="schedule-end-time" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-1">Location</label>
              <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Room 3, Lab"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-sm"
                data-testid="schedule-location" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-1">Notes</label>
              <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any extra details"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] text-sm"
                data-testid="schedule-notes" />
            </div>
            <div className="md:col-span-2 flex justify-end gap-3 pt-2">
              <button type="button" onClick={resetForm} className="px-5 py-2.5 text-[#6B7280] border border-slate-200 rounded-lg hover:bg-slate-50 text-sm font-medium">Cancel</button>
              <button type="submit" className="flex items-center gap-2 px-5 py-2.5 bg-[#0F4C5C] text-white rounded-lg hover:bg-[#0F4C5C]/90 text-sm font-medium" data-testid="schedule-save">
                <Check className="w-4 h-4" /> {editId ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length === 0 && past.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-16 text-center" data-testid="empty-schedule">
          <Calendar className="w-14 h-14 text-[#6B7280] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1F2937] mb-2">No appointments yet</h3>
          <p className="text-[#6B7280]">Click "Add Appointment" to start building your schedule</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(upcomingGroups).map(([date, dateItems]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${date === today ? 'bg-[#0F4C5C] text-white' : 'bg-slate-100 text-[#1F2937]'}`}>
                  {formatDate(date)}
                </div>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              <div className="space-y-2">
                {dateItems.map(item => (
                  <div key={item.id} className="bg-white rounded-lg border border-slate-200 px-5 py-4 flex items-center gap-4 hover:shadow-sm transition-shadow" data-testid={`schedule-item-${item.id}`}>
                    <div className="text-center min-w-[56px]">
                      <div className="text-lg font-bold text-[#0F4C5C]">{item.time}</div>
                      {item.end_time && <div className="text-xs text-[#6B7280]">to {item.end_time}</div>}
                    </div>
                    <div className="w-px h-10 bg-slate-200"></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#1F2937] truncate">{item.title}</div>
                      <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-0.5">
                        {item.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>}
                        {item.notes && <span className="truncate">{item.notes}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => handleEdit(item)} className="p-2 text-[#6B7280] hover:text-[#0F4C5C] hover:bg-slate-50 rounded-lg transition-colors" data-testid={`edit-${item.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-[#6B7280] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Past appointments */}
          {past.length > 0 && (
            <details className="mt-8">
              <summary className="cursor-pointer text-sm font-medium text-[#6B7280] hover:text-[#1F2937] mb-3">
                Past appointments ({past.length})
              </summary>
              <div className="space-y-4 opacity-60">
                {Object.entries(pastGroups).map(([date, dateItems]) => (
                  <div key={date}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-50 text-[#6B7280]">{formatDate(date)}</div>
                      <div className="flex-1 h-px bg-slate-100"></div>
                    </div>
                    <div className="space-y-2">
                      {dateItems.map(item => (
                        <div key={item.id} className="bg-white rounded-lg border border-slate-100 px-5 py-3 flex items-center gap-4">
                          <div className="text-center min-w-[56px]">
                            <div className="text-sm font-medium text-[#6B7280]">{item.time}</div>
                          </div>
                          <div className="w-px h-8 bg-slate-100"></div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-[#6B7280] truncate text-sm">{item.title}</div>
                          </div>
                          <button onClick={() => handleDelete(item.id)} className="p-1.5 text-[#6B7280] hover:text-red-500 rounded-lg transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSchedule;
